// Customer Profile page. Four sections:
//   1. Account — first/last name + read-only email
//   2. Shipping address — full editable address
//   3. Order history — all orders with the current customer + a per-order
//      "Add note" affordance that writes a customer comment to the order's
//      production timeline (and emails admin when marked urgent).
//   4. Library shortcut — quick jump back into saved designs / uploads
import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import TopBar from '../components/TopBar'
import { apiFetch } from '../lib/api'

interface ShippingShape {
  fullName: string; phone: string | null; line1: string; line2: string | null
  city: string; region: string | null; postcode: string; country: string
}
interface MeShape {
  authenticated: true
  user: {
    id: string; email: string
    firstName: string | null; lastName: string | null
    role: string; createdAt: string; lastLoginAt: string | null
  }
  shipping: ShippingShape | null
}
interface OrderRow {
  id: string; number: number; status: string
  totalCents: number; currency: string
  createdAt: string; paidAt: string | null; shippedAt: string | null
  itemCount: number; customerNoteCount: number
}

const STATUS_TONE: Record<string, string> = {
  pending_payment: 'bg-amber-100 text-amber-800',
  paid:            'bg-emerald-100 text-emerald-800',
  in_production:   'bg-blue-100 text-blue-800',
  shipped:         'bg-indigo-100 text-indigo-800',
  delivered:       'bg-teal-100 text-teal-800',
  cancelled:       'bg-red-100 text-red-700',
  refunded:        'bg-rose-100 text-rose-800',
}
function StatusChip({ status }: { status: string }) {
  return <span className={'inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ' + (STATUS_TONE[status] ?? 'bg-gray-100 text-gray-700')}>{status.replace(/_/g, ' ')}</span>
}

export default function Profile() {
  const { user: authkitUser, getAccessToken, signOut } = useAuth()
  const navigate = useNavigate()
  const [me, setMe] = useState<MeShape | null>(null)
  const [orders, setOrders] = useState<OrderRow[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  // Server-side sign-in mirror. AuthKit React's `user` can be null for a
  // beat during the SDK's in-browser hydration even when the sealed
  // cookie session is valid (e.g. right after /auth/callback redirects
  // back to /profile). Without this mirror the Navigate-to-home guard
  // below would punt the customer to the home page before AuthKit
  // catches up.
  const [serverSignedIn, setServerSignedIn] = useState<boolean | null>(null)
  useEffect(() => {
    let cancelled = false
    fetch('/api/me', { credentials: 'include' })
      .then((r) => r.json())
      .then((d: { authenticated?: boolean }) => { if (!cancelled) setServerSignedIn(!!d?.authenticated) })
      .catch(() => { if (!cancelled) setServerSignedIn(false) })
    return () => { cancelled = true }
  }, [])
  const signedIn = !!authkitUser || serverSignedIn === true

  // Account form (firstName/lastName)
  const [accountForm, setAccountForm] = useState({ firstName: '', lastName: '' })
  const [accountBusy, setAccountBusy] = useState(false)

  // Shipping form
  const [shipForm, setShipForm] = useState<ShippingShape>({
    fullName: '', phone: '', line1: '', line2: '',
    city: '', region: '', postcode: '', country: 'AU',
  })
  const [shipBusy, setShipBusy] = useState(false)

  // Per-order note composer state — { orderId: { text, urgent, busy } }
  const [noteFor, setNoteFor] = useState<string | null>(null)
  const [noteText, setNoteText] = useState('')
  const [noteUrgent, setNoteUrgent] = useState(false)
  const [noteBusy, setNoteBusy] = useState(false)

  const load = useCallback(async () => {
    if (!signedIn) return
    setLoading(true)
    try {
      const [m, o] = await Promise.all([
        apiFetch<MeShape | { authenticated: false }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/me'),
        apiFetch<{ orders: OrderRow[] }>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/me/orders?limit=100'),
      ])
      if ('authenticated' in m && m.authenticated) {
        setMe(m)
        setAccountForm({ firstName: m.user.firstName ?? '', lastName: m.user.lastName ?? '' })
        if (m.shipping) setShipForm({
          fullName: m.shipping.fullName, phone: m.shipping.phone ?? '',
          line1: m.shipping.line1, line2: m.shipping.line2 ?? '',
          city: m.shipping.city, region: m.shipping.region ?? '',
          postcode: m.shipping.postcode, country: m.shipping.country,
        })
      }
      setOrders(o.orders); setErr(null)
    } catch (e) { setErr((e as Error).message) }
    finally { setLoading(false) }
  }, [signedIn, getAccessToken])
  useEffect(() => { void load() }, [load])

  // While the server mirror is still resolving AND AuthKit hasn't surfaced
  // a user yet, render a neutral loading state instead of bouncing the
  // customer to the home page (which is what the old `!user → Navigate`
  // guard was doing during the hydration window).
  if (serverSignedIn === null && !authkitUser) {
    return <div className="min-h-screen flex flex-col bg-surface"><TopBar title="Profile" /><div className="flex-1 flex items-center justify-center text-ink-3 text-sm">Loading your profile…</div></div>
  }
  if (!signedIn) return <Navigate to="/" replace />

  async function saveAccount() {
    setAccountBusy(true)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/me', {
        method: 'PATCH', body: JSON.stringify({ firstName: accountForm.firstName, lastName: accountForm.lastName }),
      })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setAccountBusy(false) }
  }

  async function saveShipping() {
    if (!shipForm.fullName || !shipForm.line1 || !shipForm.city || !shipForm.postcode || !shipForm.country) {
      alert('Name, street, city, postcode and country are required.'); return
    }
    setShipBusy(true)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, '/api/me/shipping', {
        method: 'PATCH', body: JSON.stringify({
          fullName: shipForm.fullName, phone: shipForm.phone || null,
          line1: shipForm.line1, line2: shipForm.line2 || null,
          city: shipForm.city, region: shipForm.region || null,
          postcode: shipForm.postcode, country: shipForm.country.toUpperCase(),
        }),
      })
      await load()
    } catch (e) { alert((e as Error).message) }
    finally { setShipBusy(false) }
  }

  async function submitNote(orderId: string) {
    if (!noteText.trim()) return
    setNoteBusy(true)
    try {
      await apiFetch(getAccessToken as unknown as () => Promise<string | undefined>, `/api/me/orders/${orderId}/note`, {
        method: 'POST', body: JSON.stringify({ note: noteText.trim(), urgent: noteUrgent }),
      })
      setNoteFor(null); setNoteText(''); setNoteUrgent(false)
      await load()
      alert(noteUrgent ? 'Sent. Admin has been notified.' : 'Note added to your order.')
    } catch (e) { alert((e as Error).message) }
    finally { setNoteBusy(false) }
  }

  return (
    <div className="min-h-screen bg-surface">
      <TopBar title="Profile" />
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div>
          <div className="eyebrow">Account</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">My Profile</h1>
          <p className="text-ink-2 text-sm mt-1">Manage your account, address, orders, and saved work in one place.</p>
        </div>

        {err && <div className="card text-red-700">{err}</div>}
        {loading && <div className="text-ink-3 text-sm">Loading…</div>}

        {!loading && me && (
          <>
            {/* Account */}
            <div className="card">
              <div className="eyebrow">Account details</div>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-ink-3 block mb-1">First name</label>
                  <input className="input" value={accountForm.firstName} onChange={(e) => setAccountForm({ ...accountForm, firstName: e.target.value })} />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-ink-3 block mb-1">Last name</label>
                  <input className="input" value={accountForm.lastName} onChange={(e) => setAccountForm({ ...accountForm, lastName: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-ink-3 block mb-1">Email</label>
                  <input className="input bg-surface" value={me.user.email} disabled title="Email is managed by your sign-in provider" />
                  <p className="text-[11px] text-ink-3 mt-1">Joined {new Date(me.user.createdAt).toLocaleDateString()} · last sign-in {me.user.lastLoginAt ? new Date(me.user.lastLoginAt).toLocaleString() : 'never'}</p>
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button onClick={() => void signOut()} className="btn-outline text-xs">Sign out</button>
                <button onClick={() => void saveAccount()} disabled={accountBusy} className="btn-gold text-xs disabled:opacity-50">{accountBusy ? 'Saving…' : 'Save'}</button>
              </div>
            </div>

            {/* Shipping */}
            <div className="card">
              <div className="eyebrow">Shipping address</div>
              <p className="text-[11px] text-ink-3 mb-3">This is the default address attached to new orders. Past orders carry their own snapshot and aren't affected by updates here.</p>
              <div className="grid grid-cols-2 gap-3">
                <input className="input col-span-2" placeholder="Full name" value={shipForm.fullName} onChange={(e) => setShipForm({ ...shipForm, fullName: e.target.value })} />
                <input className="input col-span-2" placeholder="Phone (optional)" value={shipForm.phone ?? ''} onChange={(e) => setShipForm({ ...shipForm, phone: e.target.value })} />
                <input className="input col-span-2" placeholder="Street address" value={shipForm.line1} onChange={(e) => setShipForm({ ...shipForm, line1: e.target.value })} />
                <input className="input col-span-2" placeholder="Apartment / suite (optional)" value={shipForm.line2 ?? ''} onChange={(e) => setShipForm({ ...shipForm, line2: e.target.value })} />
                <input className="input" placeholder="Suburb / City" value={shipForm.city} onChange={(e) => setShipForm({ ...shipForm, city: e.target.value })} />
                <input className="input" placeholder="State" value={shipForm.region ?? ''} onChange={(e) => setShipForm({ ...shipForm, region: e.target.value })} />
                <input className="input" placeholder="Postcode" value={shipForm.postcode} onChange={(e) => setShipForm({ ...shipForm, postcode: e.target.value })} />
                <input className="input" placeholder="Country (2-letter)" maxLength={2} value={shipForm.country} onChange={(e) => setShipForm({ ...shipForm, country: e.target.value.toUpperCase() })} />
              </div>
              <div className="mt-3 flex justify-end">
                <button onClick={() => void saveShipping()} disabled={shipBusy} className="btn-gold text-xs disabled:opacity-50">{shipBusy ? 'Saving…' : 'Save address'}</button>
              </div>
            </div>

            {/* Order history + per-order notes */}
            <div className="card">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="eyebrow">Order history</div>
                  <p className="text-[11px] text-ink-3">Every order on your account. Tap an order to add a note for urgent attention.</p>
                </div>
              </div>
              {orders.length === 0 ? (
                <p className="text-sm text-ink-3">No orders yet. Hit <button onClick={() => navigate('/designer')} className="text-gold-dark hover:underline">Designer</button> or <button onClick={() => navigate('/dtf')} className="text-gold-dark hover:underline">DTF</button> to start one.</p>
              ) : (
                <ul className="divide-y divide-border text-sm">
                  {orders.map((o) => (
                    <li key={o.id} className="py-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-xs text-ink-3 shrink-0">#{o.number}</span>
                        <StatusChip status={o.status} />
                        <span className="text-[11px] text-ink-3 shrink-0">{o.itemCount} item{o.itemCount === 1 ? '' : 's'}</span>
                        {o.customerNoteCount > 0 && (
                          <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-gold/15 text-gold-dark shrink-0">
                            {o.customerNoteCount} note{o.customerNoteCount === 1 ? '' : 's'}
                          </span>
                        )}
                        <span className="ml-auto font-semibold text-ink">${(o.totalCents / 100).toFixed(2)}</span>
                        <span className="text-[11px] text-ink-3 shrink-0">{new Date(o.createdAt).toLocaleDateString()}</span>
                        <button onClick={() => { setNoteFor(noteFor === o.id ? null : o.id); setNoteText(''); setNoteUrgent(false) }}
                          className="text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded border border-border text-ink-2 hover:border-gold hover:text-ink whitespace-nowrap">
                          {noteFor === o.id ? 'Cancel' : 'Add note'}
                        </button>
                      </div>
                      {noteFor === o.id && (
                        <div className="mt-3 ml-1 border-l-2 border-gold pl-3">
                          <textarea
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            placeholder="Add context for production — e.g. delivery deadline, colour preference, anything urgent."
                            rows={3}
                            className="input" />
                          <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                            <label className="flex items-center gap-2 text-[11px] text-ink-2 cursor-pointer">
                              <input type="checkbox" checked={noteUrgent} onChange={(e) => setNoteUrgent(e.target.checked)} className="accent-red-600" />
                              <span><strong className="text-red-700">Mark urgent</strong> — emails admin immediately</span>
                            </label>
                            <button onClick={() => void submitNote(o.id)} disabled={!noteText.trim() || noteBusy}
                              className={'text-[11px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap ' +
                                (noteUrgent ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-ink text-white hover:bg-ink-2')}>
                              {noteBusy ? 'Sending…' : noteUrgent ? 'Send urgent note' : 'Send note'}
                            </button>
                          </div>
                          <p className="text-[10px] text-ink-3 mt-2">Notes are visible to the EVRST production team on the order. Urgent notes also send an email to the studio.</p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Library shortcut */}
            <div className="card">
              <div className="eyebrow">Site history</div>
              <p className="text-sm text-ink-2 mb-3">Saved designs and uploaded artwork live in your library. Open it from either builder, or jump straight in:</p>
              <div className="flex gap-2 flex-wrap">
                <Link to="/designer" className="btn-outline text-xs">Designer</Link>
                <Link to="/dtf" className="btn-outline text-xs">DTF builder</Link>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
